import React, { ReactNode, useContext } from "react"
import pushToDataLayer from "@ecom/ui/utils/pushToDataLayer"
import { StaticImage } from "gatsby-plugin-image"
import Button from "@material-ui/core/Button"
import IconButton from "@material-ui/core/IconButton"
import CircularProgress from "@material-ui/core/CircularProgress"
import Box from "@material-ui/core/Box"
import { useMediaQuery } from "@material-ui/core"
import getAdditionalData from "@ecom/ui/helpers/getAdditionalData"
import { FormikErrors, FormikProps } from "formik"
import { Arrow } from "./images/Arrow"
import useESIA from "../ESIA/useESIA"
import { PlacementsESIA } from "../ESIA/types"

import * as styles from "./esiaLogin.module.scss"
import { sendRequest } from "../../helpers/sendRequest"
import { CalcWithFormContext } from "../../context"
import { FioData, FormValues } from "../FormPKW/types"
import { formatCallTimeIso } from "../FormPKW/Form/helpers/formatCallTimeIso"
import { pushToDataLayerOnce } from "../../helpers/pushToDataLayerOnce"

const defTitle = "+60% к одобрению"

const defDescription = (
  <>
    При заполнении через Госуслуги — <br />
    быстрее и выгоднее
  </>
)

type ESIALoginProps = {
  productName?: string
  placement?: PlacementsESIA
  title?: ReactNode
  description?: ReactNode
  onValidateRequest: () => void
  errorFields: string | FormikErrors<{ data: FioData }> | null
  formik: FormikProps<FormValues>
  choiceOfTime?: boolean
}

export function ESIALogin({
  productName = "КК Халва",
  placement = "main",
  title = defTitle,
  description = defDescription,
  onValidateRequest,
  errorFields,
  formik,
  choiceOfTime,
}: ESIALoginProps) {
  const isDesktop = useMediaQuery("(min-width: 959.5px)")
  const { loanAmount, term } = useContext(CalcWithFormContext)
  const { requestStatus, handleESIAClick, isButtonDisabled } = useESIA(productName)

  const defaultSubmit = (valuesToRequest: Record<string, any>) => {
    sendRequest(`/v2/app/public/start`, { ...valuesToRequest })
      .then(({ requestId }: { requestId: string }) => {
        sessionStorage.setItem("requestId", requestId)
      })
      .finally(() => {
        if (errorFields === null) {
          if (!isDesktop && !isButtonDisabled) {
            handleESIAClick(placement)
          } else {
            handleESIAClick(placement)
          }
        }
      })
  }

  const handleClickBlock = () => {
    onValidateRequest()
    const { values } = formik
    const formattedCallTime = formatCallTimeIso ? formatCallTimeIso(values.callTime) : undefined
    const valuesToRequest = {
      agree: formik.values.agree,
      surname: values.fio.data.surname,
      name: values.fio.data.name,
      patronymic: values.fio.data.patronymic,
      phone: values.phone.replace(/[- )(+]/g, ""),
      productName,
      additionalData: getAdditionalData(),
      sum: loanAmount,
      save: false,
      phoneValidate: false,
      term,
      ...(choiceOfTime &&
        formattedCallTime && {
          callTime: formattedCallTime,
        }),
    }

    if (errorFields) {
      pushToDataLayer({
        event: "buttonClick",
        name: "enter_esia",
        placement: "calculator",
        eventLabel: "URL страницы",
        eventAction: "ESIA - форма не заполнена",
      })
    }

    if (errorFields === null) {
      if (formattedCallTime) {
        pushToDataLayerOnce("submittedCallTime", {
          event: "submittedCallTime",
          eventCategory: "success",
          eventAction: formattedCallTime,
          eventLabel: globalThis?.location?.href,
          placement: "main",
        })
      }
      defaultSubmit(valuesToRequest)
    }
  }

  const buttonContent =
    requestStatus === "loading" ? (
      <CircularProgress color="inherit" size={20} />
    ) : (
      "Заполнить через Госуслуги"
    )

  return (
    <Box className={styles.wrapper} onClick={isDesktop ? undefined : handleClickBlock}>
      <StaticImage
        className={styles.logo}
        alt="logo ESIA"
        src="./images/logoESIA.png"
        placeholder="blurred"
        formats={["auto", "webp", "avif"]}
      />
      <div className={styles.text}>
        <div className={styles.title}>{title}</div>
        <div className={styles.descr}>{description}</div>
      </div>
      {isDesktop ? (
        <Button
          variant="outlined"
          className={styles.button}
          onClick={handleClickBlock}
          disabled={isButtonDisabled}
        >
          {buttonContent}
        </Button>
      ) : (
        <IconButton
          classes={{
            root: styles.iconButtonRoot,
          }}
          disabled={isButtonDisabled}
        >
          <Arrow />
        </IconButton>
      )}
    </Box>
  )
}
